import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Upload, X } from 'lucide-react';

interface HeroImageUploadProps {
  images: string[];
  onChange: (images: string[]) => void;
}

export function HeroImageUpload({ images, onChange }: HeroImageUploadProps) {
  const [uploadingImages, setUploadingImages] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  async function handleImageUpload(files: File[]) {
    if (files.length === 0) return;
    
    setUploadingImages(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('image', file);

        const res = await fetch('/api/upload/image', {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });

        if (!res.ok) {
          throw new Error(`Failed to upload ${file.name}`);
        }

        const data = await res.json();
        return data.url;
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      onChange([...images, ...uploadedUrls]);
    } catch (error) {
      console.error('Error uploading images:', error);
      alert(`Failed to upload images: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUploadingImages(false);
    }
  }

  return (
    <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
      <CardHeader>
        <CardTitle style={{ color: '#183df7' }}>Hero Images</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Drag and drop area */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setIsDragging(false);
          }}
          onDrop={async (e) => {
            e.preventDefault();
            setIsDragging(false);
            const files = Array.from(e.dataTransfer.files).filter(
              file => file.type.startsWith('image/')
            );
            await handleImageUpload(files);
          }}
          className={`
            border-2 border-dashed rounded-lg p-8 text-center transition-colors
            ${isDragging ? 'border-[#183df7] bg-[#183df7]/5' : 'border-muted-foreground/25'}
            ${uploadingImages ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}
          `}
          onClick={() => document.getElementById('hero-image-input')?.click()}
        >
          <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-lg font-medium mb-2">
            Drag and drop images here, or click to select
          </p>
          <p className="text-sm text-muted-foreground mb-4">
            Supports multiple images (max 5MB each)
          </p>
          <Input
            id="hero-image-input"
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => {
              if (e.target.files) {
                handleImageUpload(Array.from(e.target.files));
              }
            }}
            className="hidden"
          />
        </div>

        {/* Image previews */}
        {images && images.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {images.map((url, index) => (
              <div key={index} className="relative group">
                <div className="aspect-video rounded-lg overflow-hidden border border-muted bg-muted">
                  <img
                    src={url}
                    alt={`Hero ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect width="100" height="100" fill="%23ddd"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" fill="%23999"%3EImage%3C/text%3E%3C/svg%3E';
                    }}
                  />
                </div>
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => {
                    const newImages = images.filter((_, i) => i !== index);
                    onChange(newImages);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
